import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  resolveFullUrl,
  extractRealFileName,
  extractRealFilePath,
  getExtension,
  getCompatibleTypes,
  isCompanionFileType,
  findBestMediaMatch,
  extractSamePathFile,
  getParentFolderUrl
} from '../utils/skoUrlResolver.js'
import { saveUploadedFileToDb, clearUploadedFileFromDb } from '../utils/fileBlobStorage.js'

export const usePreviewStore = defineStore('preview', () => {
  // Current preview state
  const rawUrl = ref('')
  const currentType = ref('')
  const compatibleTypes = ref([])

  // Active Preview Session Context (1 Data Chunk per active preview session)
  const activeSession = ref(null)

  // File metadata
  const fileBlob = ref(null) // in case user uploaded local file
  const uploadedFilesMap = ref(new Map()) // blobUrl -> File

  // Playlist / Folder queue state
  const playlist = ref([])
  const playlistIndex = ref(-1)
  const isPlaylistActive = ref(false)
  const playlistMetadata = ref({ title: '', format: '', isHls: false })
  const repeatMode = ref('off') // 'off' | 'all' | 'one'
  const isShuffle = ref(false)

  const currentPlaylistTrack = computed(() => {
    if (!playlist.value || playlistIndex.value < 0 || playlistIndex.value >= playlist.value.length) {
      return null
    }
    return playlist.value[playlistIndex.value]
  })

  // Computed
  const fullUrl = computed(() => resolveFullUrl(rawUrl.value))

  const fileName = computed(() => {
    if (fileBlob.value?.name) return fileBlob.value.name
    return extractRealFileName(rawUrl.value) || 'file'
  })

  const fileExt = computed(() => {
    if (fileBlob.value?.name) return getExtension(fileBlob.value.name)
    return getExtension(rawUrl.value || fullUrl.value)
  })

  const fileBadge = computed(() => {
    if (currentType.value) return currentType.value.toUpperCase()
    return fileExt.value.toUpperCase() || 'FILE'
  })

  const hasMultipleTypes = computed(() => {
    return compatibleTypes.value && compatibleTypes.value.length > 1
  })

  // Set file from URL/Query and initialize session context
  function setFile(url, explicitType = null, companionInit = null, explicitFileName = null) {
    rawUrl.value = url || ''
    const isBlob = (url || '').startsWith('blob:')
    if (!isBlob && fileBlob.value) {
      fileBlob.value = null
    }
    const effectiveTarget = explicitFileName || (isBlob && fileBlob.value?.name) || url
    const comp = getCompatibleTypes(effectiveTarget, explicitType === 'folder')
    compatibleTypes.value = comp.types

    if (explicitType && comp.types.includes(explicitType)) {
      currentType.value = explicitType
    } else {
      currentType.value = comp.defaultType || 'audio'
    }

    // Initialize session chunk
    const isRemote = (url || '').startsWith('http://') || (url || '').startsWith('https://')
    const sourceType = isBlob ? 'blob' : (isRemote ? 'remote' : 'relative')
    const realName = explicitFileName || (isBlob && fileBlob.value?.name) || extractRealFileName(url) || 'file'

    activeSession.value = {
      sessionId: `session_${Date.now()}`,
      primary: {
        rawInput: url,
        realName,
        realPath: extractRealFilePath(url),
        fullUrl: resolveFullUrl(url),
        ext: getExtension(realName),
        viewerType: currentType.value,
        sourceType
      },
      companions: companionInit || {
        lyrics: null,
        cursor: null,
        cover: null
      }
    }
  }

  // Reverse Companion Inference: Try finding matching media for an auxiliary file
  async function checkReverseCompanion(url, siblingItems = []) {
    if (!url || typeof url !== 'string') return null
    const ext = getExtension(url)
    if (!isCompanionFileType(ext)) return null

    const realName = extractRealFileName(url)
    if (!realName) return null
    const baseName = realName.replace(/\.[^/.]+$/, '')
    const isMidiAux = ['lyc', 'lyr', 'cur'].includes(ext.toLowerCase())

    let items = Array.isArray(siblingItems) && siblingItems.length > 0
      ? siblingItems
      : (Array.isArray(playlist.value) && playlist.value.length > 0 ? playlist.value : [])

    // If no sibling items passed and it's a remote URL, fetch directory autoindex
    if (items.length === 0 && !url.startsWith('blob:')) {
      const full = resolveFullUrl(url)
      const parentFolder = getParentFolderUrl(full)
      if (parentFolder && (parentFolder.startsWith('http://') || parentFolder.startsWith('https://') || parentFolder.startsWith('/'))) {
        try {
          const res = await fetch(`${parentFolder}?type=json`)
          if (res.ok) {
            const data = await res.json()
            if (Array.isArray(data)) items = data
          }
        } catch {}
      }
    }

    if (items.length === 0) return null

    const match = findBestMediaMatch(baseName, items, isMidiAux)
    if (!match) return null

    const matchedRelativePath = extractSamePathFile(url, match.name || match.relativePath)
    const matchComp = getCompatibleTypes(match.name || match.relativePath)

    return {
      matchItem: match,
      matchedUrl: matchedRelativePath,
      matchedType: isMidiAux ? 'midi' : (matchComp?.defaultType || 'audio'),
      companionUrl: url,
      companionExt: ext
    }
  }

  // Switch type (e.g. from user clicking switcher on toolbar)
  function switchType(newType) {
    if (compatibleTypes.value.includes(newType)) {
      currentType.value = newType
      if (activeSession.value?.primary) {
        activeSession.value.primary.viewerType = newType
      }
    }
  }

  // Register an uploaded local file and get a blob url
  function registerUploadedFile(file) {
    const blobUrl = URL.createObjectURL(file)
    fileBlob.value = file
    uploadedFilesMap.value.set(blobUrl, file)
    setFile(blobUrl, null, null, file.name)
    saveUploadedFileToDb(file).catch(err => console.warn('[previewStore] Failed to cache file to IndexedDB:', err))
    return blobUrl
  }

  // Clear uploaded local file state and release blob URLs
  function clearUploadedFile() {
    fileBlob.value = null
    for (const [blobUrl] of uploadedFilesMap.value) {
      try {
        URL.revokeObjectURL(blobUrl)
      } catch {}
    }
    uploadedFilesMap.value.clear()
    clearUploadedFileFromDb('current_upload').catch(() => {})
  }

  // Set playlist for sequential media playback
  function setPlaylist(items, startIndex = 0, meta = null) {
    playlist.value = items || []
    playlistIndex.value = startIndex
    const isActual = Boolean(meta?.isActualPlaylist || (currentType.value === 'playlist' && meta))
    isPlaylistActive.value = Boolean(items && items.length > 0 && isActual)
    playlistMetadata.value = meta || { title: '', format: '', isHls: false }
  }

  // Clear playlist queue and reset active status
  function clearPlaylist() {
    playlist.value = []
    playlistIndex.value = -1
    isPlaylistActive.value = false
    playlistMetadata.value = { title: '', format: '', isHls: false }
  }


  function playTrackAtIndex(idx) {
    if (!playlist.value || idx < 0 || idx >= playlist.value.length) return null
    playlistIndex.value = idx
    return playlist.value[idx]
  }

  function playNextInPlaylist(forceNext = false) {
    if (!playlist.value || playlist.value.length === 0) return null
    if (repeatMode.value === 'one' && !forceNext) {
      return playlist.value[playlistIndex.value]
    }

    if (isShuffle.value && playlist.value.length > 1) {
      let randIdx
      let attempts = 0
      do {
        randIdx = Math.floor(Math.random() * playlist.value.length)
        attempts++
      } while (randIdx === playlistIndex.value && attempts < 10)
      playlistIndex.value = randIdx
      return playlist.value[randIdx]
    }

    if (playlistIndex.value < playlist.value.length - 1) {
      playlistIndex.value++
      return playlist.value[playlistIndex.value]
    } else if (repeatMode.value === 'all') {
      playlistIndex.value = 0
      return playlist.value[0]
    }
    return null
  }

  function playPreviousInPlaylist() {
    if (!playlist.value || playlist.value.length === 0) return null
    if (playlistIndex.value > 0) {
      playlistIndex.value--
      return playlist.value[playlistIndex.value]
    } else if (repeatMode.value === 'all') {
      playlistIndex.value = playlist.value.length - 1
      return playlist.value[playlistIndex.value]
    }
    return null
  }

  function toggleShuffle() {
    isShuffle.value = !isShuffle.value
  }

  function cycleRepeatMode() {
    if (repeatMode.value === 'off') repeatMode.value = 'all'
    else if (repeatMode.value === 'all') repeatMode.value = 'one'
    else repeatMode.value = 'off'
  }

  return {
    rawUrl,
    currentType,
    compatibleTypes,
    activeSession,
    fileBlob,
    playlist,
    playlistIndex,
    isPlaylistActive,
    playlistMetadata,
    repeatMode,
    isShuffle,
    currentPlaylistTrack,
    fullUrl,
    fileName,
    fileExt,
    fileBadge,
    hasMultipleTypes,
    setFile,
    checkReverseCompanion,
    switchType,
    registerUploadedFile,
    clearUploadedFile,
    setPlaylist,
    clearPlaylist,
    playTrackAtIndex,
    playNextInPlaylist,
    playPreviousInPlaylist,
    toggleShuffle,
    cycleRepeatMode
  }
})
